import React, { useState, useEffect } from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import './HalfYearPicker.css';

const HalfYearPicker = React.forwardRef<any, DatePickerProps>((props, ref) => {
  const {
    placeholder = 'Выберите полугодие',
    value,
    onChange,
    disabledDate: externalDisabledDate,
    ...restProps
  } = props;

  const [currentYear, setCurrentYear] = useState<number>(
    value ? dayjs(value).year() : dayjs().year()
  );

  useEffect(() => {
    if (value) {
      setCurrentYear(dayjs(value).year());
    }
  }, [value]);

  const handleYearChange = (newYear: number) => {
    setCurrentYear(newYear);
  };

  const h1Date = dayjs(`${currentYear}-01-01`);
  const h2Date = dayjs(`${currentYear}-07-01`);

  const currentValue = value ? dayjs(value) : null;
  const isH1Selected = currentValue?.year() === currentYear && currentValue.month() === 0;
  const isH2Selected = currentValue?.year() === currentYear && currentValue.month() === 6;

  // Кастомная функция форматирования
  const formatDate = (date: Dayjs | null | undefined): string => {
    if (!date || !date.isValid()) return '';
    const half = date.month() < 6 ? 'H1' : 'H2';
    return `${date.year()} ${half}`; // 2026 H1
  };

  const handleSelect = (selectedDate: Dayjs) => {
    const formattedString = formatDate(selectedDate);
    onChange?.(selectedDate, formattedString);
  };

  // Комбинированный disabledDate — полностью совместим с Ant Design
  const combinedDisabledDate: DatePickerProps['disabledDate'] = (current, info) => {
    if (!current || !current.isValid()) return false;

    // 1. Пользовательский disabledDate (если передан)
    if (externalDisabledDate) {
      if (externalDisabledDate(current, info)) {
        return true;
      }
    }

    // 2. Наша логика — только H1 и H2
    const month = current.month();
    return month !== 0 && month !== 6;
  };

  // Вспомогательная функция, чтобы не дублировать вызов с двумя аргументами
  const isDateDisabled = (date: Dayjs): boolean => {
    return combinedDisabledDate(date, undefined as any); // второй аргумент dummy
  };

  const panelRender: DatePickerProps['panelRender'] = () => (
    <div className="ant-picker-panel half-year-panel">
      <div className="ant-picker-header">
        <button
          type="button"
          className="ant-picker-header-prev-btn"
          onClick={() => handleYearChange(currentYear - 1)}
        >
          <button type="button" aria-label="Предыдущий год (Control + left)" tabIndex={-1}
                  className="ant-picker-header-super-prev-btn"><span className="ant-picker-super-prev-icon"></span>
          </button>
        </button>
        <div className="ant-picker-header-view">{currentYear}</div>
        <button
          type="button"
          className="ant-picker-header-next-btn"
          onClick={() => handleYearChange(currentYear + 1)}
        >
          <button type="button" aria-label="Следующий год (Control + right)" tabIndex={-1}
                  className="ant-picker-header-super-next-btn"><span className="ant-picker-super-next-icon"></span>
          </button>
        </button>
      </div>

      <div className="ant-picker-content">
        <table className="ant-picker-content">
          <tbody>
          <tr>
            <td
              className={`ant-picker-cell ant-picker-cell-in-view ${
                isH1Selected ? 'ant-picker-cell-selected' : ''
              } ${isDateDisabled(h1Date) ? 'ant-picker-cell-disabled' : ''}`}
              onClick={() => !isDateDisabled(h1Date) && handleSelect(h1Date)}
              style={{
                cursor: isDateDisabled(h1Date) ? 'not-allowed' : 'pointer'
              }}
            >
              <div className="ant-picker-cell-inner half-year-cell">H1</div>
            </td>

            <td
              className={`ant-picker-cell ant-picker-cell-in-view ${
                isH2Selected ? 'ant-picker-cell-selected' : ''
              } ${isDateDisabled(h2Date) ? 'ant-picker-cell-disabled' : ''}`}
              onClick={() => !isDateDisabled(h2Date) && handleSelect(h2Date)}
              style={{
                cursor: isDateDisabled(h2Date) ? 'not-allowed' : 'pointer'
              }}
            >
              <div className="ant-picker-cell-inner half-year-cell">H2</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <DatePicker
      ref={ref}
      {...restProps}
      value={value}
      onChange={onChange}
      picker="month"
      format={formatDate}
      placeholder={placeholder}
      panelRender={panelRender}
      disabledDate={combinedDisabledDate}
      popupClassName="half-year-picker-popup"
    />
  );
});

HalfYearPicker.displayName = 'HalfYearPicker';

export default HalfYearPicker;
